import { isEventKey, codes } from 'keycode';
import React__default, { useRef, useEffect, createElement, createRef, useState, forwardRef, Fragment, Children } from 'react';
import ReactGA from 'react-ga';
import styled from 'styled-components';
import md5 from 'crypto-js/md5';
import { useAnimation, motion } from 'framer-motion';
import { v1 } from 'uuid';

var isKey = function isKey(e, keyCode) {
  if (isEventKey(e.nativeEvent, keyCode)) {
    e.preventDefault();
    return true;
  } else {
    return false;
  }
};
var isDownArrow = function isDownArrow(e) {
  return isKey(e, codes.down);
};
var isEnd = function isEnd(e) {
  return isKey(e, codes.end);
};
var isEnter = function isEnter(e) {
  return isKey(e, codes.enter);
};
var isEscape = function isEscape(e) {
  return isKey(e, codes.esc);
};
var isHome = function isHome(e) {
  return isKey(e, codes.home);
};
var isLeftArrow = function isLeftArrow(e) {
  return isKey(e, codes.left);
};
var isMinus = function isMinus(e) {
  return isKey(e, codes['-']) || isKey(e, codes['numpad -']);
};
var isPageDown = function isPageDown(e) {
  return isKey(e, codes['page down']);
};
var isPageUp = function isPageUp(e) {
  return isKey(e, codes['page up']);
};
var isPeriod = function isPeriod(e) {
  return isKey(e, codes['.']) || isKey(e, codes['numpad .']);
};
var isRightArrow = function isRightArrow(e) {
  return isKey(e, codes.right);
};
var isSpace = function isSpace(e) {
  return isKey(e, codes.space);
};
var isUpArrow = function isUpArrow(e) {
  return isKey(e, codes.up);
};

var googleAnalytics = function googleAnalytics(category) {
  return {
    logEvent: function logEvent(action, label) {
      ReactGA.event({
        category: category,
        action: action,
        label: label
      });
    },
    logPerformance: function logPerformance(action, label, seconds) {
      ReactGA.event({
        category: category,
        action: action,
        label: label,
        value: seconds
      });
    },
    pageView: function pageView(name) {
      return ReactGA.pageview(category + "/" + name);
    }
  };
};

var analyticsContext = /*#__PURE__*/React__default.createContext( /*#__PURE__*/googleAnalytics('starryUtils'));
var AnalyticsProvider = function AnalyticsProvider(_ref) {
  var children = _ref.children,
      category = _ref.category,
      gaCode = _ref.gaCode;
  ReactGA.initialize(gaCode);
  return React__default.createElement(analyticsContext.Provider, {
    value: googleAnalytics(category)
  }, children);
};

var useAnalytics = function useAnalytics() {
  return React__default.useContext(analyticsContext);
};

var usePerformance = function usePerformance(loading, action, name) {
  var _useAnalytics = useAnalytics(),
      logPerformance = _useAnalytics.logPerformance;

  var date1 = useRef(new Date());
  useEffect(function () {
    if (!loading && date1.current) {
      var date2 = new Date();
      var difference = date2.getTime() - date1.current.getTime();
      logPerformance(action, name, difference);
    }
  }, [loading, action, name, logPerformance]);
  return {};
};

var isSentryRunning = function isSentryRunning() {
  return typeof Sentry !== 'undefined';
};

var useSentry = function useSentry() {
  return {
    captureException: function captureException(err) {
      if (isSentryRunning() && Sentry.captureException) {
        Sentry.captureException(err);
      }
    },
    captureMessage: function captureMessage(msg) {
      if (isSentryRunning() && Sentry.captureMessage) {
        Sentry.captureMessage(msg);
      }
    }
  };
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var color = {
  // Primary Palette
  StarRezBrandColors: {
    Blue60: '#0076E0',
    Grey30: '#DAE5EC',
    Grey80: '#12374E',
    WhiteSR: 'white',
    YellowSR: '#FFF100'
  },
  Grey: {
    G100: '#020F18',
    G90: '#072131',
    // tslint:disable-next-line: object-literal-sort-keys
    G80: '#12374E',
    G70: '#516878',
    G60: '#6D748D',
    G50: '#C4C9D2',
    G40: '#C8D3DA',
    G30: '#DAE5EC',
    G20: '#EDF2F5',
    G10: '#F6F8FA'
  },
  // Secondary Palette
  Blue: {
    B100: '#00398D',
    B80: '#0341E0',
    // tslint:disable-next-line: object-literal-sort-keys
    B60: '#0075DB',
    B40: '#B4E1FA',
    B20: '#EBF6FF'
  },
  Green: {
    G100: '#003A1B',
    G80: '#038215',
    // tslint:disable-next-line: object-literal-sort-keys
    G60: '#50B83C',
    G40: '#BBE5B3',
    G20: '#E3F1DF'
  },
  Orange: {
    O100: '#4A1504',
    O80: '#C05717',
    // tslint:disable-next-line: object-literal-sort-keys
    O60: '#FB7C39',
    O40: '#FFC58B',
    O20: '#FCEBDB'
  },
  Pink: {
    P100: '#58002C',
    P80: '#A21950',
    // tslint:disable-next-line: object-literal-sort-keys
    P60: '#D22866',
    P40: '#FEA0C0',
    P20: '#FFCFE2'
  },
  Purple: {
    P100: '#1F0481',
    P80: '#4319D6',
    // tslint:disable-next-line: object-literal-sort-keys
    P60: '#6A43F3',
    P40: '#DED6FC',
    P20: '#F6F0FD'
  },
  Red: {
    R100: '#4F0407',
    R80: '#A5191F',
    // tslint:disable-next-line: object-literal-sort-keys
    R60: '#E1272F',
    R40: '#FEAD9A',
    R20: '#FBEAE5'
  },
  Teal: {
    T100: '#003135',
    T80: '#00828C',
    // tslint:disable-next-line: object-literal-sort-keys
    T60: '#47C1BF',
    T40: '#B7ECEC',
    T20: '#E0F5F5'
  },
  Yellow: {
    Y100: '#573B00',
    Y80: '#8A6116',
    // tslint:disable-next-line: object-literal-sort-keys
    Y60: '#EEC200',
    Y40: '#FFF0B3',
    Y20: '#FFFBEC'
  }
};

var fontFamily = 'Roboto, Arial, Helvetica, sans-serif';

var fontStyles = function fontStyles(size, weight) {
  if (weight === void 0) {
    weight = 'normal';
  }

  return {
    fontFamily: fontFamily,
    fontStyle: 'normal',
    fontWeight: weight,
    fontSize: "" + size,
    lineHeight: '130%',
    letterSpacing: '0.02em',
    color: color.Grey.G80
  };
};

var FontTypes = ['xSmall.regular', 'small.regular', 'small.medium', 'small.bold', 'small.allCaps', 'normal.regular', 'normal.medium', 'normal.bold', 'normal.allCaps', 'intermediate.medium', 'large.regular', 'large.medium', 'large.bold', 'large.allCaps', 'xLarge.regular', 'xLarge.medium', 'xLarge.bold', 'xxxLarge.bold'];
var fontTypeToObject = function fontTypeToObject(type) {
  var item = type.split('.');
  return font[item[0]][item[1]];
};
var font = {
  xSmall: {
    regular: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('9px'))
  },
  small: {
    regular: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('12px')),
    medium: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('12px', 500)),
    bold: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('12px', 'bold')),
    allCaps: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('12px', 'bold'), {
      textTransform: 'uppercase'
    })
  },
  normal: {
    regular: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('14px')),
    medium: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('14px', 500)),
    bold: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('14px', 'bold')),
    allCaps: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('14px', 'bold'), {
      textTransform: 'uppercase'
    })
  },
  intermediate: {
    medium: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('15px', 500))
  },
  large: {
    regular: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('16px')),
    medium: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('16px', 500)),
    bold: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('16px', 'bold')),
    allCaps: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('16px', 'bold'), {
      textTransform: 'uppercase'
    })
  },
  xLarge: {
    regular: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('20px')),
    medium: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('20px', 500)),
    bold: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('20px', 'bold'))
  },
  xxxLarge: {
    bold: /*#__PURE__*/_extends({}, /*#__PURE__*/fontStyles('48px', 'bold'))
  }
};

var scheme = {
  // Primary Palette
  StarRezBrandColors: {
    BlueRegular: {
      backgroundColor: color.StarRezBrandColors.Blue60,
      color: 'white'
    },
    WhiteRegular: {
      backgroundColor: color.StarRezBrandColors.WhiteSR,
      color: color.StarRezBrandColors.Grey80
    },
    // tslint:disable-next-line: object-literal-sort-keys
    GreyDarker: {
      backgroundColor: color.StarRezBrandColors.Grey80,
      color: 'white'
    },
    GreyLighter: {
      backgroundColor: color.StarRezBrandColors.Grey30,
      color: color.StarRezBrandColors.Grey80
    },
    YellowRegular: {
      backgroundColor: color.StarRezBrandColors.YellowSR,
      color: color.StarRezBrandColors.Grey80
    }
  },
  Grey: {
    Darkest: {
      backgroundColor: color.Grey.G100,
      color: 'white'
    },
    UltraDark: {
      backgroundColor: color.Grey.G90,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Darker: {
      backgroundColor: color.Grey.G80,
      color: 'white'
    },
    Dark: {
      backgroundColor: color.Grey.G70,
      color: 'white'
    },
    RegularDark: {
      backgroundColor: color.Grey.G60,
      color: 'white'
    },
    RegularLight: {
      backgroundColor: color.Grey.G50,
      color: color.Grey.G80
    },
    Light: {
      backgroundColor: color.Grey.G40,
      color: color.Grey.G80
    },
    Lighter: {
      backgroundColor: color.Grey.G30,
      color: color.Grey.G80
    },
    UltraLight: {
      backgroundColor: color.Grey.G20,
      color: color.Grey.G80
    },
    Lightest: {
      backgroundColor: color.Grey.G10,
      color: color.Grey.G80
    }
  },
  // Secondary Palette
  Blue: {
    Darker: {
      backgroundColor: color.Blue.B100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Blue.B80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Blue.B60,
      color: 'white'
    },
    Light: {
      backgroundColor: color.Blue.B40,
      color: color.Blue.B100
    },
    Lighter: {
      backgroundColor: color.Blue.B20,
      color: color.Blue.B100
    }
  },
  Green: {
    Darker: {
      backgroundColor: color.Green.G100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Green.G80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Green.G60,
      color: color.Green.G100
    },
    Light: {
      backgroundColor: color.Green.G40,
      color: color.Green.G100
    },
    Lighter: {
      backgroundColor: color.Green.G20,
      color: color.Green.G100
    }
  },
  Orange: {
    Darker: {
      backgroundColor: color.Orange.O100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Orange.O80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Orange.O60,
      color: color.Orange.O100
    },
    Light: {
      backgroundColor: color.Orange.O40,
      color: color.Orange.O100
    },
    Lighter: {
      backgroundColor: color.Orange.O20,
      color: color.Orange.O100
    }
  },
  Pink: {
    Darker: {
      backgroundColor: color.Pink.P100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Pink.P80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Pink.P60,
      color: 'white'
    },
    Light: {
      backgroundColor: color.Pink.P40,
      color: color.Pink.P100
    },
    Lighter: {
      backgroundColor: color.Pink.P20,
      color: color.Pink.P100
    }
  },
  Purple: {
    Darker: {
      backgroundColor: color.Purple.P100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Purple.P80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Purple.P60,
      color: 'white'
    },
    Light: {
      backgroundColor: color.Purple.P40,
      color: color.Purple.P100
    },
    Lighter: {
      backgroundColor: color.Purple.P20,
      color: color.Grey.G80
    }
  },
  Red: {
    Darker: {
      backgroundColor: color.Red.R100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Red.R80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Red.R60,
      color: 'white'
    },
    Light: {
      backgroundColor: color.Red.R40,
      color: color.Red.R100
    },
    Lighter: {
      backgroundColor: color.Red.R20,
      color: color.Red.R100
    }
  },
  Teal: {
    Darker: {
      backgroundColor: color.Teal.T100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Teal.T80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Teal.T60,
      color: color.Teal.T100
    },
    Light: {
      backgroundColor: color.Teal.T40,
      color: color.Teal.T100
    },
    Lighter: {
      backgroundColor: color.Teal.T20,
      color: color.Teal.T100
    }
  },
  Yellow: {
    Darker: {
      backgroundColor: color.Yellow.Y100,
      color: 'white'
    },
    // tslint:disable-next-line: object-literal-sort-keys
    Dark: {
      backgroundColor: color.Yellow.Y80,
      color: 'white'
    },
    Regular: {
      backgroundColor: color.Yellow.Y60,
      color: color.Yellow.Y100
    },
    Light: {
      backgroundColor: color.Yellow.Y40,
      color: color.Yellow.Y100
    },
    Lighter: {
      backgroundColor: color.Yellow.Y20,
      color: color.Yellow.Y100
    }
  }
};

var size = {
  none: '0',
  xxxSmall: '2px',
  xxSmall: '4px',
  xSmall: '8px',
  small: '16px',
  medium: '24px',
  large: '40px',
  xLarge: '56px',
  xxLarge: '72px',
  xxxLarge: '90px'
};

var styles = {
  borderRadius: '4px'
};

var _templateObject, _templateObject2;
var iconSpacing = '7px';
var IconWrapper = /*#__PURE__*/styled.span(_templateObject || (_templateObject = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    position: relative;\n    display: flex;\n    align-items: center;\n    height: 18px;\n    width: 18px;\n    ", "\n\n    svg {\n        position: absolute;\n        top: 50%;\n        left: 50%;\n        transform: translate(-50%, -50%);\n    }\n"])), function (_ref) {
  var iconPosition = _ref.iconPosition;
  return iconPosition === 'Right' ? 'order: 1;' : '';
});
var StyledButton = /*#__PURE__*/styled.button(_templateObject2 || (_templateObject2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    display: grid;\n    grid-auto-flow: column;\n    grid-auto-columns: min-content;\n    align-items: center;\n    justify-content: center;\n    gap: ", ";\n    white-space: nowrap;\n    padding: ", ";\n    width: ", ";\n    border-radius: ", ";\n    cursor: pointer;\n    border: 0;\n    outline: 0;\n\n    svg,\n    span {\n        cursor: pointer;\n    }\n\n    &:disabled {\n        cursor: not-allowed;\n    }\n\n    &:not(:disabled):hover {\n        outline: 0;\n    }\n\n    &:focus {\n        outline: 2px solid ", ";\n        outline-offset: -4px;\n    }\n\n    ", "\n"])), iconSpacing, function (_ref2) {
  var compact = _ref2.compact;
  return compact ? '7px 12px' : '11px 20px';
}, function (_ref3) {
  var fullWidth = _ref3.fullWidth;
  return fullWidth ? '100%' : 'auto';
}, function () {
  return styles.borderRadius;
}, color.StarRezBrandColors.WhiteSR, function (props) {
  return buttonStateStyles(props);
});

var buttonStateStyles = function buttonStateStyles(_ref4) {
  var variant = _ref4.variant,
      compact = _ref4.compact;

  if (variant === 'Primary') {
    return "\n            color: " + color.StarRezBrandColors.WhiteSR + ";\n            background-color: " + color.Blue.B60 + ";\n        \n            &:disabled {\n                background-color: " + color.Grey.G50 + ";\n            }\n        \n            &:not(:disabled):hover {\n                background-color: " + color.Blue.B80 + ";\n            }\n        \n            &:focus {\n                background-color: " + color.Blue.B80 + ";\n            }\n        \n            &:not(:disabled):active {\n                background-color: " + color.Blue.B100 + ";\n            }\n        ";
  }

  if (variant === 'Secondary') {
    return "\n            color: " + color.Blue.B60 + ";\n            background-color: " + color.Grey.G20 + ";\n        \n            &:disabled {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Grey.G50 + ";\n            }\n        \n            &:not(:disabled):hover {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B80 + ";\n            }\n        \n            &:focus {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B80 + ";\n            }\n        \n            &:not(:disabled):active {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B100 + ";\n            }\n        ";
  }

  if (variant === 'Tertiary') {
    return "\n            padding: " + (compact ? '6px 12px' : '10px 20px') + ";\n            color: " + color.Blue.B60 + ";\n            background-color: " + color.StarRezBrandColors.WhiteSR + ";\n            border: 1px solid " + color.Grey.G40 + ";\n        \n            &:disabled {\n                color: " + color.Grey.G50 + ";\n                background-color: " + color.StarRezBrandColors.WhiteSR + ";\n            }\n        \n            &:not(:disabled):hover {\n                color: " + color.Blue.B80 + ";\n                background-color: " + color.Grey.G10 + ";\n            }\n        \n            &:focus {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B80 + ";\n                border: 1px solid " + color.Blue.B80 + ";\n            }\n        \n            &:not(:disabled):active {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B100 + ";\n                border: 1px solid " + color.Blue.B100 + ";\n            }\n        ";
  }

  if (variant === 'Danger') {
    return "\n            color: " + color.StarRezBrandColors.WhiteSR + ";\n            background-color: " + color.Red.R60 + ";\n        \n            &:disabled {\n                background-color: " + color.Grey.G50 + ";\n            }\n        \n            &:not(:disabled):hover {\n                background-color: " + color.Red.R80 + ";\n            }\n        \n            &:focus {\n                background-color: " + color.Red.R80 + ";\n            }\n        \n            &:not(:disabled):active {\n                background-color: " + color.Red.R100 + ";\n            }\n        ";
  }

  if (variant === 'Ghost') {
    return "\n            color: " + color.Blue.B60 + ";\n            background-color: transparent;\n        \n            &:disabled {\n                color: " + color.Grey.G50 + ";\n            }\n        \n            &:not(:disabled):hover {\n                color: " + color.Blue.B80 + ";\n                background-color: " + color.Grey.G20 + ";\n            }\n        \n            &:focus {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B80 + ";\n            }\n        \n            &:not(:disabled):active {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B100 + ";\n            }\n        ";
  }

  if (variant === 'SecondaryReversed') {
    return "\n            color: " + color.Blue.B60 + ";\n            background-color: " + color.StarRezBrandColors.WhiteSR + ";\n        \n            &:disabled {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Grey.G50 + ";\n            }\n        \n            &:not(:disabled):hover {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B80 + ";\n            }\n        \n            &:focus {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B80 + ";\n            }\n        \n            &:not(:disabled):active {\n                color: " + color.StarRezBrandColors.WhiteSR + ";\n                background-color: " + color.Blue.B100 + ";\n            }\n        ";
  }

  return '';
};

var IconWrapper$1 = function IconWrapper(_ref) {
  var children = _ref.children,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '24px' : _ref$height,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? '24px' : _ref$width,
      ariaLabel = _ref.ariaLabel;
  return createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: width,
    height: height,
    viewBox: "0 0 24 24",
    "aria-label": ariaLabel
  }, children);
};

var AccountIcon = function AccountIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M3 12C3 7.02954 7.03002 3 12.0005 3C16.9716 3 21 7.02954 21 12C21 16.9705 16.9709 21 12.0005 21C7.03002 21 3 16.9705 3 12ZM12.8127 16.5968C13.2126 16.486 13.6133 16.3284 13.9519 16.0907L13.9519 16.0907C14.6706 15.6016 15.0902 14.8901 15.0884 14.1156C15.0686 13.3572 14.6291 12.6629 13.8898 12.1729L11.3717 10.5464C11.0709 10.341 10.9313 10.1041 10.9313 9.80423C10.9115 9.50342 11.0511 9.25124 11.351 9.04589C11.6509 8.84055 11.9904 8.7775 12.3695 8.82524C12.7496 8.87296 13.0495 9.03058 13.2296 9.29897C13.5295 9.66193 14.0897 9.78892 14.549 9.58358C15.0083 9.34672 15.1875 8.9045 14.8885 8.54155C14.5085 7.98857 13.869 7.59318 13.1297 7.41936V6.78801C13.1297 6.3611 12.71 6.02967 12.1705 6.02967C11.6311 6.02967 11.2114 6.362 11.2114 6.78801V7.40405C10.8124 7.51484 10.4324 7.65714 10.1127 7.894C9.3931 8.38304 8.99415 9.07833 9.01396 9.82046C9.01396 10.5626 9.43363 11.257 10.173 11.7154L12.6722 13.342C12.9919 13.5311 13.1522 13.831 13.172 14.1318C13.172 14.4632 13.0117 14.7316 12.692 14.937C12.3921 15.1432 12.0328 15.2216 11.6131 15.1747C11.2132 15.127 10.9133 14.9541 10.6936 14.6695C10.4342 14.3056 9.85515 14.1795 9.39494 14.3849C8.93654 14.6226 8.77534 15.0649 9.03561 15.4278C9.45528 16.0123 10.0749 16.3915 10.8944 16.5968V17.2129C10.8944 17.6398 11.3141 17.9712 11.8536 17.9712C12.393 17.9712 12.8127 17.6398 12.8127 17.2129V16.5968Z"
  }));
};

var AgentBookingIcon = function AgentBookingIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M9.27101079,14 L5,14 L5,13.0557264 C5,12.5969933 5.29692661,12.2075905 5.7086264,12.0696682 C5.74104204,12.0586815 7.84876215,11.5203346 8.1500835,10.9215874 C8.2876742,10.6480281 8.15201719,10.2975796 8.01467259,10.0558728 C7.95179257,9.97128426 7.89026613,9.88173849 7.83148201,9.78779806 C7.83148201,9.78725312 7.83118316,9.78725312 7.83118316,9.78725312 C7.47415401,9.21598136 7.20194345,8.46452819 7.17420382,7.53563588 C7.13219005,6.13818401 8.21350847,5.00328721 9.4969852,5 L9.50304995,5 C10.7867728,5.00328721 11.8681088,6.13818401 11.8263587,7.53560072 C11.7986542,8.46447545 11.52618,9.21592862 11.1690981,9.78721796 L11.1690981,9.7877629 C11.1099976,9.88170333 11.0484887,9.97123152 10.9856263,10.0558377 C10.8482992,10.2975445 10.7126246,10.6479753 10.8502153,10.9215522 C10.9786315,11.1765312 11.4345104,11.420545 11.9181829,11.6175451 L9.27101079,14 Z M10,14.6239199 L14.0265777,11 L16.3508253,13.0916815 L16.3508253,11.4686509 L16.6331362,11.4686509 L16.6331362,13.3460304 L18.0530843,14.6239199 L17.5770209,14.6239199 L17.5770209,19.207329 L15.2727027,19.207329 L15.2727027,16.7558491 L12.780541,16.7558491 L12.780541,19.207329 L10.4761118,19.207329 L10.4761118,14.6239199 L10,14.6239199 Z"
  }));
};

var ArrowLeftIcon = function ArrowLeftIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9.0322 16C9.02604 16 8.11628 15.1012 7.0105 14.0027C5.90473 12.9042 5 12.003 5 12C5 11.9892 9.02127 8 9.03209 8C9.03816 8 9.24112 8.19673 9.4831 8.43718L9.92308 8.87435L8.34442 10.4426L7.44784 11.3333H17.4786V12.6667H7.42583L8.33878 13.5736L9.9118 15.1363L9.4776 15.5682C9.23879 15.8057 9.03836 16 9.0322 16Z"
  }));
};

var ArrowRightIcon = function ArrowRightIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M14.9678 8C14.974 8 15.8837 8.89878 16.9895 9.99728C18.0953 11.0958 19 11.997 19 12C19 12.0108 14.9787 16 14.9679 16C14.9618 16 14.7589 15.8033 14.5169 15.5628L14.0769 15.1256L15.6556 13.5574L16.5522 12.6667L6.52137 12.6667V11.3333L16.5742 11.3333L15.6612 10.4264L14.0882 8.8637L14.5224 8.43185C14.7612 8.19433 14.9616 8 14.9678 8Z"
  }));
};

var CaretDownIcon = function CaretDownIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M16.7143 9.60714C16.7143 9.77158 16.6542 9.91388 16.534 10.034L12.284 14.284C12.1639 14.4042 12.0216 14.4643 11.8571 14.4643C11.6927 14.4643 11.5504 14.4042 11.4302 14.284L7.18025 10.034C7.06008 9.91388 7 9.77158 7 9.60714C7 9.44271 7.06008 9.30041 7.18025 9.18025C7.30041 9.06008 7.44271 9 7.60714 9H16.1071C16.2716 9 16.4139 9.06008 16.534 9.18025C16.6542 9.30041 16.7143 9.44271 16.7143 9.60714Z"
  }));
};

var CaretRightIcon = function CaretRightIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M9.73214 6.875C9.89658 6.875 10.0389 6.93508 10.159 7.05525L14.409 11.3052C14.5292 11.4254 14.5893 11.5677 14.5893 11.7321C14.5893 11.8966 14.5292 12.0389 14.409 12.159L10.159 16.409C10.0389 16.5292 9.89658 16.5893 9.73214 16.5893C9.56771 16.5893 9.42541 16.5292 9.30525 16.409C9.18508 16.2889 9.125 16.1466 9.125 15.9821V7.48214C9.125 7.31771 9.18508 7.17541 9.30525 7.05525C9.42541 6.93508 9.56771 6.875 9.73214 6.875Z"
  }));
};

var ChevronDownIcon = function ChevronDownIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M7.99455812,12.4606243 C5.79755185,10.387468 4,8.68179901 4,8.67025011 C4,8.65870122 4.38866534,8.28292732 4.86369921,7.83519683 L5.72739666,7.02114245 L8.85277011,9.97030854 L11.9781423,12.9194763 L15.1147174,9.95973814 L18.2512922,7 L19.125646,7.82488055 C19.6065408,8.27856452 20,8.65907578 20,8.67046297 C20,8.69075068 12.0215156,16.2299995 12.000045,16.2299995 C11.9940339,16.2299995 10.1915644,14.5337805 7.99455812,12.4606243 Z"
  }));
};

var ClearFiltersIcon = function ClearFiltersIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    d: "M17.5265 6.39209L10.5674 13.3512V18.4546C10.5674 18.9649 10.9153 19.1505 11.3393 18.8675L12.5792 18.0405C13.0042 17.7587 13.351 17.1092 13.351 16.5988V12.8873L18.2056 8.03274C19.1074 7.1298 18.8023 6.39209 17.5265 6.39209H17.5265Z",
    fill: "currentColor"
  }), createElement("path", {
    d: "M15.584 5.55064C15.5394 5.45031 15.4819 5.35463 15.3996 5.27228C15.0366 4.90924 14.4503 4.90924 14.0878 5.27228L12.9674 6.39212H6.39215C5.1163 6.39212 4.81014 7.1298 5.71306 8.03277L8.51988 10.8396L5.27228 14.0872C4.90924 14.4502 4.90924 15.0365 5.27228 15.399C5.45322 15.5799 5.6904 15.671 5.92818 15.671C6.16596 15.671 6.40314 15.5799 6.58408 15.399L15.399 6.58407C15.6791 6.30396 15.7371 5.89221 15.584 5.55064H15.584Z",
    fill: "currentColor"
  }));
};

var CloseThickIcon = function CloseThickIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M14,12 L18,16 L16,18 L12,14 L8,18 L6,16 L10,12 L6,8 L8,6 L12,10 L16,6 L18,8 L14,12 Z"
  }));
};

var ColumnIcon = function ColumnIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M19.6596 6H4.34043C4.15292 6 4 6.14668 4 6.32653V17.102C4 17.2819 4.15293 17.4286 4.34043 17.4286H19.6596C19.8471 17.4286 20 17.2819 20 17.102V6.32653C20 6.14668 19.8471 6 19.6596 6ZM4.68085 8.33486H11.6596V16.7755H4.68085V8.33486ZM12.3404 16.7755H19.3191V8.33486H12.3404V16.7755Z"
  }));
};

var EllipsesHorizontalIcon = function EllipsesHorizontalIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M6,14 C4.8954305,14 4,13.1045695 4,12 C4,10.8954305 4.8954305,10 6,10 C7.1045695,10 8,10.8954305 8,12 C8,13.1045695 7.1045695,14 6,14 Z M18,14 C16.8954305,14 16,13.1045695 16,12 C16,10.8954305 16.8954305,10 18,10 C19.1045695,10 20,10.8954305 20,12 C20,13.1045695 19.1045695,14 18,14 Z M12,14 C10.8954305,14 10,13.1045695 10,12 C10,10.8954305 10.8954305,10 12,10 C13.1045695,10 14,10.8954305 14,12 C14,13.1045695 13.1045695,14 12,14 Z"
  }));
};

var FolderIcon = function FolderIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M5.33333333,5 L8.44444444,5 C8.86412084,5 9.25930527,5.20077917 9.51111111,5.54193548 L10.0444444,6.26451613 C10.2962503,6.60567244 10.6914347,6.80645161 11.1111111,6.80645161 L18.6666667,6.80645161 C19.4030463,6.80645161 20,7.41303356 20,8.16129032 L20,17.6451613 C20,18.393418 19.4030463,19 18.6666667,19 L5.33333333,19 C4.59695367,19 4,18.393418 4,17.6451613 L4,6.35483871 C4,5.60658195 4.59695367,5 5.33333333,5 Z"
  }));
};

var MailIcon = function MailIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M12.4978367,16.9963426 C10.7893297,16.9963426 9.0808226,16.9963426 7.37231554,16.9963426 C6.30594072,17.0173606 5.36267423,16.3399687 5.08847447,15.3562383 C5.03524692,15.1696073 5.00752507,14.9771917 5.00599827,14.7837761 C5,13.22811 5,11.6695815 5,10.1124843 C5,9.70889839 5.26392385,9.55862705 5.64331439,9.74753959 C7.77270002,10.8037324 9.90058608,11.8623105 12.0269726,12.9232738 C12.3178667,13.087398 12.6793063,13.087398 12.9702004,12.9232738 C15.0865898,11.8680351 17.2054785,10.8156587 19.3268664,9.76614461 C19.3884056,9.73340949 19.4545668,9.7093092 19.5233097,9.69458683 C19.8142258,9.64306523 19.9926743,9.78188732 19.9926743,10.0623938 C19.9926743,11.4105424 19.9926743,12.759168 19.9926743,14.1082706 C20.0098505,14.4579019 19.9968101,14.8082894 19.9536856,15.1558765 C19.7755226,16.2274865 18.7991009,17.0111653 17.6623467,16.9949114 C16.2707482,17.0063607 14.8806492,16.9949114 13.4890507,16.9949114 L12.4978367,16.9949114 L12.4978367,16.9963426 Z M12.4785411,6.0008209 C14.2155692,6.0008209 15.9510682,6.0008209 17.6880963,6.0008209 C18.7079887,6.0008209 19.4618222,6.44410683 19.9236025,7.33067868 C20.07651,7.61585929 19.9985272,7.80203938 19.7018868,7.95127898 C17.3980819,9.10776271 15.0947868,10.2662166 12.7920013,11.4266406 C12.614813,11.5244531 12.3973158,11.5244531 12.2201276,11.4266406 L5.24907906,7.92024896 C4.99678184,7.79317366 4.94326425,7.63506835 5.05641573,7.38682823 C5.49831813,6.47513684 6.25062255,6.01264186 7.2842766,6.00525376 C9.0151884,5.9934328 10.7461002,6.00525376 12.4831283,6.00525376 L12.4785411,6.0008209 Z"
  }));
};

var PaperclipIcon = function PaperclipIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    d: "M6.17165 6.1715C6.92146 5.42108 7.9392 5 9.00023 5C10.0611 5 11.0789 5.42108 11.8288 6.1715L17.8285 12.1724C18.5789 12.9222 19 13.94 19 15.0004C19 16.0613 18.5789 17.0784 17.8285 17.8284C17.0787 18.5788 16.0616 18.9999 15.0005 18.9999C14.4482 18.9999 14.0004 18.552 14.0004 17.9997C14.0004 17.4474 14.4482 16.9995 15.0005 16.9995C15.8129 17.002 16.5464 16.5134 16.8563 15.763C17.1662 15.0126 16.9918 14.1485 16.4145 13.5773L10.4149 7.58542C9.63346 6.80401 8.36716 6.80401 7.58566 7.58542C6.80425 8.36683 6.80425 9.63312 7.58566 10.4146L12.0158 14.8437C12.1361 14.9433 12.288 14.9986 12.4448 14.9992C12.5943 15.0059 12.7407 14.9549 12.8537 14.8577C13.0482 14.662 13.0482 14.3466 12.8537 14.151L9.85327 11.1463C9.4808 10.7526 9.4893 10.134 9.87271 9.75119C10.2555 9.36779 10.8741 9.35988 11.2672 9.73236L14.2677 12.7328C14.7356 13.2019 14.9981 13.8375 14.9981 14.4998C14.9981 15.1621 14.7356 15.7977 14.2677 16.2667C13.7804 16.7406 13.1247 17.0019 12.4448 16.9946H12.429H12.4296C11.7466 16.9977 11.0897 16.7333 10.5999 16.2576L6.1715 11.8285C5.42108 11.0787 5 10.0609 5 8.99992C5 7.939 5.42108 6.92126 6.1715 6.17134L6.17165 6.1715Z",
    fill: "currentColor"
  }));
};

var PlusIcon = function PlusIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    d: "M18.9944 10.9972H12.9816V5C12.9816 4.45 12.5316 4 11.9816 4C11.4316 4 10.9816 4.45 10.9816 5V10.9972H5C4.45 10.9972 4 11.4472 4 11.9972C4 12.5472 4.45 12.9972 5 12.9972H10.9816V18.9944C10.9816 19.5444 11.4316 19.9944 11.9816 19.9944C12.5316 19.9944 12.9816 19.5444 12.9816 18.9944V12.9972H18.9944C19.5444 12.9972 19.9944 12.5472 19.9944 11.9972C19.9944 11.4472 19.5444 10.9972 18.9944 10.9972V10.9972Z",
    fill: "currentColor"
  }));
};

var PrintIcon = function PrintIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("g", {
    fill: "currentColor",
    transform: "translate(5 5)"
  }, createElement("path", {
    d: "M10.8045506 3.46122186L10.8045506 2.68634507 10.8045506 1.54389649C10.7949438 1.47471955 10.7890449 1.38971263 10.7890449 1.28175227 10.7890449.219245016 10.041573.0280190382 9.65342697 0L4.97814607 0C3.9544382 0 3.93910112.865107591 3.93910112.865107591L3.93910112 2.68650337 3.93910112 3.46153846 10.2500562 3.46153846 10.8045506 3.46122186zM10.0683708 9.49261452L4.75146067 9.49261452C4.05606742 9.49261452 4.01797753 10.1650864 4.01797753 10.1650864L4.01797753 10.8839067 4.01797753 11.6172213 4.01797753 12.0842437C4.02168539 12.2258168 4.0791573 12.7636258 4.73376404 12.7636258L10.088764 12.7636258C10.2480337 12.7595808 10.8626966 12.6977269 10.8626966 11.9888505L10.8626966 10.2571089C10.8626966 9.62340104 10.3402247 9.50946845 10.0683708 9.49261452z"
  }), createElement("path", {
    d: "M12.0881461,1.79070441 L12.0310112,1.79070441 C11.8879213,1.80368194 11.5238764,1.9282325 11.5238764,2.86177182 L11.5238764,4.11958081 L11.2936517,4.11958081 L3.45033708,4.11958081 L3.35730337,4.11958081 L3.35730337,2.61570441 C3.35730337,1.96716508 3.09977528,1.81868194 2.93292135,1.79053587 L2.79337079,1.79053587 C1.25983146,1.79053587 0.0119662921,3.03806396 0.0119662921,4.5721089 L0.0119662921,8.83547969 C0.0119662921,10.3695246 1.25983146,11.6170527 2.79337079,11.6170527 C3.23275281,11.6170527 3.33016854,11.3209291 3.34820225,11.1358729 L3.34820225,10.8837381 L3.34820225,9.22716508 C3.37095506,9.04025497 3.49044944,8.74986171 4.01797753,8.74986171 L4.50691011,8.74986171 L10.3742697,8.74986171 L10.9095506,8.74986171 C11.0600562,8.75660328 11.5324719,8.84053587 11.5324719,9.64581677 L11.5324719,10.8837381 L11.5323034,10.8837381 L11.5323034,10.9594123 L11.5323034,11.0443561 C11.5408989,11.221828 11.6132022,11.6170527 12.0879775,11.6170527 C13.6216854,11.6170527 14.8692135,10.3693561 14.8692135,8.83547969 L14.8692135,4.5721089 C14.869382,3.0382325 13.6216854,1.79070441 12.0881461,1.79070441 Z M11.7623596,6.2237943 L11.3027528,6.2237943 C11.1001685,6.2237943 10.9358427,6.05946845 10.9358427,5.85688418 C10.9358427,5.65463699 11.1,5.49014261 11.3027528,5.49014261 L11.7623596,5.49014261 C11.9646067,5.49014261 12.1291011,5.65446845 12.1291011,5.85688418 C12.1291011,6.05963699 11.9647753,6.2237943 11.7623596,6.2237943 Z"
  })));
};

var RefreshIcon = function RefreshIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("g", {
    fill: "none"
  }, createElement("path", {
    fill: "currentColor",
    fillRule: "nonzero",
    d: "M14.7911186 15.6724138C14.056705 16.1854204 13.195005 16.457199 12.2991917 16.4576528L12.2389298 16.4565941C12.1196082 16.4556866 12.0002867 16.4493345 11.883069 16.438899L11.7388011 16.4205989C11.6459287 16.4089534 11.5530562 16.395493 11.4628888 16.3787054 11.4235157 16.3714459 11.3844432 16.3617665 11.3455209 16.3520871L11.298183 16.3415003C11.2095184 16.3203267 11.1219057 16.2996068 11.0356455 16.2735935L10.9121162 16.2326074C10.8135331 16.1987296 10.7138981 16.1645493 10.6169681 16.1244707L10.5902185 16.1881428 10.5523481 16.095735C10.4397892 16.0455233 10.3285827 15.9934967 10.2053539 15.9280097 9.84032608 15.7264065 9.50745802 15.477314 9.19998711 15.1690865 9.1090683 15.0783424 9.02265785 14.9833636 8.94030494 14.8830913L8.88665532 14.9280097 8.88860895 14.8176044C8.3448994 14.137931 8.02645813 13.3313672 7.95492531 12.4711131 8.68843728 12.4508469 9.33568908 12.3979129 9.3979046 12.2734422 9.42029618 12.2286751 9.47304412 12.1240169 8.89537151 11.1902601 8.62712343 10.7554446 7.47553518 9 7.1707693 9 6.84060626 9.00983061 5.45608551 11.238052 5.12998 11.9024501 4.97429092 12.2206594 4.98901827 12.285239 5.01982547 12.3272837 5.09256052 12.4260436 5.77137093 12.4635511 6.4138138 12.4765578 6.48128909 13.5426497 6.83038729 14.5626134 7.43706375 15.4401089L7.50604183 15.4209014 7.46201007 15.4850272C7.51325522 15.5577737 7.56870819 15.6262855 7.62386059 15.6941924L7.75986312 15.8682698C7.86716235 15.9978826 7.98122414 16.1208409 8.09799095 16.2409256L8.13120261 16.2752571C8.52207838 16.6666667 8.96750544 17.0013612 9.4538084 17.2673926L9.49543569 17.291591C9.63534548 17.3656987 9.7785614 17.4333031 9.92463262 17.4978826L10.0323827 17.545977C10.156964 17.5981549 10.2844006 17.6424682 10.4396389 17.6944949 10.4913349 17.7127949 10.5434817 17.7300363 10.5966805 17.7465215 10.7092395 17.7803993 10.8243532 17.8082275 10.9406692 17.8357532L10.9941685 17.8487598C11.0532282 17.8622202 11.1119873 17.8758318 11.2074145 17.8956443 11.2280027 17.9013914 11.2481401 17.9056261 11.2672255 17.9091047 11.3543874 17.9249849 11.4426012 17.935118 11.5306647 17.9452511L11.7137045 17.9683908C11.9104198 17.9874471 12.1066842 18 12.3021972 18 13.5087876 18 14.6743518 17.6312765 15.6726554 16.930732 16.0196497 16.6878403 16.1042568 16.2076528 15.8614059 15.8610103 15.62667 15.5267695 15.1265414 15.4352692 14.7911186 15.6724138zM19.9875248 11.751038C19.9276494 11.6348857 19.3382702 11.5817618 18.6486451 11.5592517 18.5942129 10.4720124 18.2408575 9.43054374 17.6171558 8.53899255L17.5593972 8.57725977 17.5911493 8.491121C17.5111643 8.37872042 17.4252825 8.27397329 17.3309334 8.16097244L17.3009957 8.12180481C16.6986132 7.4013306 15.9539512 6.85028263 15.0868168 6.48141664L15.015299 6.45035266C14.8784626 6.39482767 14.7387534 6.34560552 14.5979858 6.29923466L14.5444608 6.28152669C14.5122552 6.27057176 14.4788399 6.25901656 14.445727 6.2495623 14.3229522 6.21369616 14.1989679 6.18308239 14.0727155 6.15486969L14.0148058 6.14151368C13.9647584 6.12995848 13.9145599 6.11810315 13.8634541 6.10804862L13.7623011 6.08508829C13.7028793 6.07458356 13.6437599 6.06843079 13.583431 6.06212796L13.2869269 6.02581162C13.1289225 6.01140513 12.9727325 6.00570257 12.7784399 6.00090041L12.7162965 6C11.4988308 6.00090041 10.3276323 6.36646491 9.33091911 7.05527487 9.16127224 7.17202741 9.04802313 7.34700615 9.01188623 7.54794658 8.97620293 7.748887 9.02141185 7.95117803 9.14010418 8.11865339 9.37537204 8.45015257 9.87962544 8.53914261 10.2178608 8.30473713 10.9557188 7.79525787 11.8217949 7.52603672 12.7627151 7.52603672 12.8938058 7.52693712 13.0220238 7.53308989 13.1482762 7.54509529L13.2648517 7.55950178C13.369936 7.57195738 13.4730547 7.58681407 13.5748125 7.60632285L13.7083225 7.63693662C13.8085684 7.65854635 13.9077559 7.68240708 14.0048265 7.71061978L14.09721 7.74183383C14.2084935 7.77769996 14.3185674 7.81686759 14.4582766 7.87524386 15.0964936 8.14506528 15.6680312 8.56795558 16.1196668 9.10744835 16.6995204 9.80346156 17.0304982 10.6468411 17.0936999 11.5490471 16.4169269 11.5595518 15.66032 11.5946676 15.5821493 11.6988145 15.5513044 11.7408334 15.5367892 11.804462 15.6929792 12.1193037 16.021689 12.779901 17.4146984 14.9909959 17.7471882 15 18.0545786 15 19.1882794 13.2941824 19.475409 12.8373768 20.0635786 11.9012556 20.0105073 11.7963584 19.9875248 11.751038z"
  })));
};

var SaveIcon = function SaveIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M15.9648043,5 L6.45493848,5 C5.6525884,5 5,5.6525884 5,6.45493848 L5,17.5450615 C5,18.3474116 5.6525884,19 6.45493848,19 L17.5450615,19 C18.3474116,19 19,18.3474116 19,17.5450615 L19,8.53259062 L15.9648043,5 Z M12,17 C10.8953684,17 10,16.1046316 10,15 C10,13.8953684 10.8953684,13 12,13 C13.1046316,13 14,13.8953684 14,15 C14,16.1046316 13.1046316,17 12,17 Z M14,10 L7.00471123,10 L7.00471123,7 L14,7 L14,10 Z"
  }));
};

var SearchIcon = function SearchIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    d: "M15.2857143,11.2857143 C15.2857143,10.1845183 14.8943492,9.24256344 14.1116071,8.45982143 C13.3288651,7.67707942 12.3869103,7.28571429 11.2857143,7.28571429 C10.1845183,7.28571429 9.24256344,7.67707942 8.45982143,8.45982143 C7.67707942,9.24256344 7.28571429,10.1845183 7.28571429,11.2857143 C7.28571429,12.3869103 7.67707942,13.3288651 8.45982143,14.1116071 C9.24256344,14.8943492 10.1845183,15.2857143 11.2857143,15.2857143 C12.3869103,15.2857143 13.3288651,14.8943492 14.1116071,14.1116071 C14.8943492,13.3288651 15.2857143,12.3869103 15.2857143,11.2857143 Z M19.8571429,18.7142857 C19.8571429,19.0238111 19.7440488,19.2916655 19.5178571,19.5178571 C19.2916655,19.7440487 19.0238111,19.8571429 18.7142857,19.8571429 C18.3928555,19.8571429 18.1250011,19.7440487 17.9107143,19.5178571 L14.8482143,16.4642857 C13.7827328,17.2023846 12.5952446,17.5714286 11.2857143,17.5714286 C10.4345196,17.5714286 9.6205396,17.4062517 8.84375,17.0758929 C8.0669604,16.7455341 7.39732424,16.29911 6.83482143,15.7366071 C6.27231862,15.1741043 5.82589451,14.5044682 5.49553571,13.7276786 C5.16517692,12.950889 5,12.136909 5,11.2857143 C5,10.4345196 5.16517692,9.6205396 5.49553571,8.84375 C5.82589451,8.0669604 6.27231862,7.39732424 6.83482143,6.83482143 C7.39732424,6.27231862 8.0669604,5.82589451 8.84375,5.49553571 C9.6205396,5.16517692 10.4345196,5 11.2857143,5 C12.136909,5 12.950889,5.16517692 13.7276786,5.49553571 C14.5044682,5.82589451 15.1741043,6.27231862 15.7366071,6.83482143 C16.29911,7.39732424 16.7455341,8.0669604 17.0758929,8.84375 C17.4062517,9.6205396 17.5714286,10.4345196 17.5714286,11.2857143 C17.5714286,12.5952446 17.2023846,13.7827328 16.4642857,14.8482143 L19.5267857,17.9107143 C19.7470249,18.1309535 19.8571429,18.3988079 19.8571429,18.7142857 Z"
  }));
};

var StarFilledIcon = function StarFilledIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M17.8865 9.34179C18.1066 9.37564 18.2801 9.4772 18.407 9.64647C18.534 9.81574 18.5932 10.0019 18.5847 10.2051C18.5763 10.4082 18.4959 10.5859 18.3435 10.7383L15.6521 13.3535L16.2869 17.0605C16.3208 17.2636 16.2827 17.4541 16.1726 17.6318C16.0626 17.8095 15.906 17.9238 15.7029 17.9746C15.4998 18.0254 15.3051 18 15.1189 17.8984L11.7928 16.1718L8.46661 17.8984C8.28041 18 8.08575 18.0254 7.88262 17.9746C7.6795 17.9238 7.52292 17.8095 7.4129 17.6318C7.30287 17.4541 7.26479 17.2636 7.29864 17.0605L7.9334 13.3535L5.242 10.7383C5.08966 10.5859 5.00926 10.4082 5.00079 10.2051C4.99233 10.0019 5.05157 9.81574 5.17853 9.64647C5.30548 9.4772 5.47898 9.37564 5.69903 9.34179L9.40606 8.80858L11.0564 5.45703C11.158 5.25391 11.3061 5.11849 11.5008 5.05078C11.6954 4.98307 11.8901 4.98307 12.0848 5.05078C12.2794 5.11849 12.4275 5.25391 12.5291 5.45703L14.1795 8.80858L17.8865 9.34179Z"
  }));
};

var StarOutlineIcon = function StarOutlineIcon(props) {
  return createElement(IconWrapper$1, Object.assign({}, props), createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M17.8865 9.34179C18.1066 9.37564 18.2801 9.4772 18.407 9.64647C18.534 9.81574 18.5932 10.0019 18.5847 10.2051C18.5763 10.4082 18.4959 10.5859 18.3435 10.7383L15.6521 13.3535L16.2869 17.0605C16.3208 17.2636 16.2827 17.4541 16.1726 17.6318C16.0626 17.8095 15.906 17.9238 15.7029 17.9746C15.4998 18.0254 15.3051 18 15.1189 17.8984L11.7928 16.1718L8.46661 17.8984C8.28041 18 8.08575 18.0254 7.88262 17.9746C7.6795 17.9238 7.52292 17.8095 7.4129 17.6318C7.30287 17.4541 7.26479 17.2636 7.29864 17.0605L7.9334 13.3535L5.242 10.7383C5.08966 10.5859 5.00926 10.4082 5.00079 10.2051C4.99233 10.0019 5.05157 9.81574 5.17853 9.64647C5.30548 9.4772 5.47898 9.37564 5.69903 9.34179L9.40606 8.80858L11.0564 5.45703C11.158 5.25391 11.3061 5.11849 11.5008 5.05078C11.6954 4.98307 11.8901 4.98307 12.0848 5.05078C12.2794 5.11849 12.4275 5.25391 12.5291 5.45703L14.1795 8.80858L17.8865 9.34179ZM14.3572 12.9219L16.8963 10.4336L13.367 9.92577L11.7928 6.72656L10.2186 9.92577L6.68927 10.4336L9.22832 12.9219L8.64434 16.4511L11.7928 14.7754L14.9412 16.4511L14.3572 12.9219Z"
  }));
};

var Icon = function Icon(_ref) {
  var icon = _ref.icon,
      props = _objectWithoutPropertiesLoose(_ref, ["icon"]);

  return React__default.createElement(React__default.Fragment, null, icon === 'Account' && React__default.createElement(AccountIcon, Object.assign({}, props)), icon === 'AgentBooking' && React__default.createElement(AgentBookingIcon, Object.assign({}, props)), icon === 'ArrowLeft' && React__default.createElement(ArrowLeftIcon, Object.assign({}, props)), icon === 'ArrowRight' && React__default.createElement(ArrowRightIcon, Object.assign({}, props)), icon === 'CaretDown' && React__default.createElement(CaretDownIcon, Object.assign({}, props)), icon === 'CaretRight' && React__default.createElement(CaretRightIcon, Object.assign({}, props)), icon === 'ChevronDown' && React__default.createElement(ChevronDownIcon, Object.assign({}, props)), icon === 'ClearFilters' && React__default.createElement(ClearFiltersIcon, Object.assign({}, props)), icon === 'CloseThick' && React__default.createElement(CloseThickIcon, Object.assign({}, props)), icon === 'Column' && React__default.createElement(ColumnIcon, Object.assign({}, props)), icon === 'EllipsesHorizontal' && React__default.createElement(EllipsesHorizontalIcon, Object.assign({}, props)), icon === 'Folder' && React__default.createElement(FolderIcon, Object.assign({}, props)), icon === 'Mail' && React__default.createElement(MailIcon, Object.assign({}, props)), icon === 'Paperclip' && React__default.createElement(PaperclipIcon, Object.assign({}, props)), icon === 'Plus' && React__default.createElement(PlusIcon, Object.assign({}, props)), icon === 'Print' && React__default.createElement(PrintIcon, Object.assign({}, props)), icon === 'Refresh' && React__default.createElement(RefreshIcon, Object.assign({}, props)), icon === 'Save' && React__default.createElement(SaveIcon, Object.assign({}, props)), icon === 'Search' && React__default.createElement(SearchIcon, Object.assign({}, props)), icon === 'StarFilled' && React__default.createElement(StarFilledIcon, Object.assign({}, props)), icon === 'StarOutline' && React__default.createElement(StarOutlineIcon, Object.assign({}, props)));
};

var AttributeName = 'data-hash';

var buildHash = function buildHash(content) {
  if (content) {
    var ref = md5(content);
    return ref.toString();
  }

  return '';
};

var _setHashAtt = function setHashAtt(addHash, content) {
  if (addHash) {
    var _ref;

    return _ref = {}, _ref[AttributeName] = buildHash(content), _ref;
  }

  return {};
};
/**
 *
 * @param addHash A boolean to decide if to run this useEffect hook and add the hash
 * @param caption An optional caption that overrides the hook looking at the text content
 * @returns A ref to add to a control so the attribute can be added
 */


function useHash(addHash) {
  if (addHash === void 0) {
    addHash = false;
  }

  var ref = createRef();
  useEffect(function () {
    if (addHash) {
      var _ref$current;

      (_ref$current = ref.current) == null ? void 0 : _ref$current.setAttribute(AttributeName, buildHash(ref.current.textContent));
    } else {
      var _ref$current2;

      (_ref$current2 = ref.current) == null ? void 0 : _ref$current2.removeAttribute(AttributeName);
    }
  }, [addHash, ref]);
  return {
    ref: ref,
    setHashAtt: function setHashAtt(val) {
      return _setHashAtt(addHash, val);
    }
  };
}

var _templateObject$1, _templateObject2$1, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10;
var reset = {
  padding: 0,
  margin: 0
};
var Paragraph = /*#__PURE__*/styled.p(_templateObject$1 || (_templateObject$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Span = /*#__PURE__*/styled.span(_templateObject2$1 || (_templateObject2$1 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Small = /*#__PURE__*/styled.small(_templateObject3 || (_templateObject3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Strong = /*#__PURE__*/styled.strong(_templateObject4 || (_templateObject4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Heading1 = /*#__PURE__*/styled.h1(_templateObject5 || (_templateObject5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Heading2 = /*#__PURE__*/styled.h2(_templateObject6 || (_templateObject6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Heading3 = /*#__PURE__*/styled.h3(_templateObject7 || (_templateObject7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Heading4 = /*#__PURE__*/styled.h4(_templateObject8 || (_templateObject8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Heading5 = /*#__PURE__*/styled.h5(_templateObject9 || (_templateObject9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Heading6 = /*#__PURE__*/styled.h6(_templateObject10 || (_templateObject10 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    ", ";\n"])), function (props) {
  return _extends({}, props.prop);
});
var Text = function Text(_ref) {
  var _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'normal.medium' : _ref$variant,
      _ref$renderAs = _ref.renderAs,
      renderAs = _ref$renderAs === void 0 ? 'span' : _ref$renderAs,
      color = _ref.color,
      children = _ref.children,
      addHash = _ref.addHash;

  var styles = _extends({}, reset, fontTypeToObject(variant), {
    color: color
  });

  var _useHash = useHash(addHash),
      ref = _useHash.ref;

  switch (renderAs) {
    case 'p':
      return React__default.createElement(Paragraph, {
        ref: ref,
        prop: styles
      }, children);

    case 'h1':
      return React__default.createElement(Heading1, {
        ref: ref,
        prop: styles
      }, children);

    case 'h2':
      return React__default.createElement(Heading2, {
        ref: ref,
        prop: styles
      }, children);

    case 'h3':
      return React__default.createElement(Heading3, {
        ref: ref,
        prop: styles
      }, children);

    case 'h4':
      return React__default.createElement(Heading4, {
        ref: ref,
        prop: styles
      }, children);

    case 'h5':
      return React__default.createElement(Heading5, {
        ref: ref,
        prop: styles
      }, children);

    case 'h6':
      return React__default.createElement(Heading6, {
        ref: ref,
        prop: styles
      }, children);

    case 'strong':
      return React__default.createElement(Strong, {
        ref: ref,
        prop: styles
      }, children);

    case 'small':
      return React__default.createElement(Small, {
        ref: ref,
        prop: styles
      }, children);

    default:
      return React__default.createElement(Span, {
        ref: ref,
        prop: styles
      }, children);
  }
};

var iconSize = '22px';
var Button = /*#__PURE__*/React__default.forwardRef(function (props, ref) {
  var addHash = props.addHash,
      ariaLabel = props.ariaLabel,
      children = props.children,
      disabled = props.disabled,
      icon = props.icon,
      iconPosition = props.iconPosition,
      _props$type = props.type,
      type = _props$type === void 0 ? 'button' : _props$type,
      _props$variant = props.variant,
      variant = _props$variant === void 0 ? 'Primary' : _props$variant,
      onClick = props.onClick,
      buttonProps = _objectWithoutPropertiesLoose(props, ["addHash", "ariaLabel", "children", "disabled", "icon", "iconPosition", "type", "variant", "onClick"]);

  var _useState = useState(false),
      loading = _useState[0],
      setLoading = _useState[1];

  var handleClick = function handleClick(e) {
    if (onClick && !disabled && !loading) {
      // If this returns something then it is a promise
      var clickResponse = onClick(e);

      if (clickResponse) {
        setLoading(true);
        clickResponse["finally"](function () {
          setLoading(false);
        });
      }
    }
  };

  return React__default.createElement(StyledButton, Object.assign({
    "aria-label": ariaLabel,
    disabled: disabled,
    ref: ref,
    type: type,
    variant: variant,
    onClick: handleClick
  }, buttonProps), icon && React__default.createElement(IconWrapper, {
    iconPosition: iconPosition
  }, React__default.createElement(Icon, {
    icon: icon,
    height: iconSize,
    width: iconSize,
    ariaLabel: children
  })), children && React__default.createElement(Text, {
    addHash: addHash,
    variant: "normal.bold"
  }, loading ? 'Loading' : children));
});

var _templateObject$2;
var InternalButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var ariaLabel = _ref.ariaLabel,
      icon = _ref.icon,
      caption = _ref.caption,
      value = _ref.value,
      compact = _ref.compact,
      isSelected = _ref.isSelected,
      onClick = _ref.onClick;
  return createElement(Switcher, {
    ref: ref,
    type: "button",
    "aria-label": ariaLabel,
    icon: icon,
    compact: Boolean(compact),
    tabIndex: isSelected ? 0 : -1,
    "aria-pressed": isSelected ? true : false,
    value: value,
    variant: "Ghost",
    onClick: onClick
  }, caption);
});
var Switcher = /*#__PURE__*/styled(Button)(_templateObject$2 || (_templateObject$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    background-color: ", ";\n    border-radius: 0;\n    transition: background-color ease-in-out 0.15s; //inherit from v8\n\n    &:hover {\n        color: ", ";\n    }\n\n    &[aria-pressed='true'],\n    &:hover[aria-pressed='true'] {\n        color: ", ";\n    }\n\n    &[aria-pressed='true'] {\n        background-color: ", ";\n    }\n\n    &:hover[aria-pressed='true'] {\n        background-color: ", ";\n    }\n\n    &:first-of-type {\n        border-top-left-radius: ", ";\n        border-bottom-left-radius: ", ";\n    }\n    &:last-of-type {\n        border-top-right-radius: ", ";\n        border-bottom-right-radius: ", ";\n    }\n"])), color.Grey.G20, color.Blue.B80, color.StarRezBrandColors.WhiteSR, color.Blue.B60, color.Blue.B80, function () {
  return "" + styles.borderRadius;
}, function () {
  return "" + styles.borderRadius;
}, function () {
  return "" + styles.borderRadius;
}, function () {
  return "" + styles.borderRadius;
});

var _templateObject$3;
var SwitcherButton = function SwitcherButton() {
  return createElement(Fragment, null);
};
var ContentSwitcher = function ContentSwitcher(_ref) {
  var children = _ref.children,
      defaultValue = _ref.defaultValue,
      onClick = _ref.onClick,
      _ref$compact = _ref.compact,
      compact = _ref$compact === void 0 ? false : _ref$compact;
  var switchers = Children.toArray(children).filter(function (child) {
    return child.props.value;
  }).map(function (child) {
    var props = child.props;
    return _extends({}, props, {
      key: props.value,
      ref: createRef()
    });
  }); // if no defaultValue passed in, use the select the first item

  var _React$useState = useState(defaultValue ? defaultValue : switchers.length > 0 ? switchers[0].value : null),
      selected = _React$useState[0],
      setSelected = _React$useState[1];

  if (switchers.length === 0) {
    return createElement("div", {
      style: _extends({}, scheme.Red.Lighter, {
        padding: '8px'
      })
    }, createElement(Text, null, "This component requires a SwitcherButton element"));
  }

  var handleOnClick = function handleOnClick(value) {
    setSelected(value);
    onClick(value);
  };

  var handleOnKeyDown = function handleOnKeyDown(e) {
    var target = e.target;

    if (isSpace(e)) {
      target.click();
    } else if (isRightArrow(e)) {
      var nextTarget = target.nextSibling;

      if (nextTarget) {
        nextTarget.focus();
      }
    } else if (isLeftArrow(e)) {
      var prevTarget = target.previousSibling;

      if (prevTarget) {
        prevTarget.focus();
      }
    }
  };

  return createElement(ContentSwitcherGroup, {
    onKeyDown: handleOnKeyDown
  }, children && switchers.map(function (switcher) {
    return createElement(InternalButton, Object.assign({}, switcher, {
      compact: compact,
      isSelected: selected === switcher.value,
      onClick: function onClick() {
        return handleOnClick(switcher.value);
      }
    }));
  }));
};
var ContentSwitcherGroup = /*#__PURE__*/styled.div(_templateObject$3 || (_templateObject$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    display: inline-flex;\n    gap: 1px;\n"])));

var pixelUnit = function pixelUnit(n) {
  return "calc(8px * " + n + ")";
};

var buildPixelSize = function buildPixelSize(size) {
  switch (size) {
    default:
    case 'none':
      return '0';

    case 'xxSmall':
      return pixelUnit(0.5);

    case 'xSmall':
      return pixelUnit(1);

    case 'small':
      return pixelUnit(2);

    case 'medium':
      return pixelUnit(3);

    case 'large':
      return pixelUnit(5);

    case 'xLarge':
      return pixelUnit(7);

    case 'xxLarge':
      return pixelUnit(9);
  }
};

var _templateObject$4;
var Stack = function Stack(_ref) {
  var children = _ref.children,
      styles = _objectWithoutPropertiesLoose(_ref, ["children"]);

  return createElement(StackedWrapper, {
    styled: styles
  }, children);
};
var StackedWrapper = /*#__PURE__*/styled.div(_templateObject$4 || (_templateObject$4 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    display: ", ";\n    gap: ", ";\n    padding: ", ";\n    align-items: ", ";\n    justify-content: ", ";\n    justify-items: ", ";\n    grid-auto-flow: ", ";\n    grid-auto-columns: ", ";\n    grid-auto-rows: ", ";\n    grid-template-columns: ", ";\n    height: ", ";\n    max-width: ", ";\n    ", "\n"])), function (props) {
  return props.styled.inline ? 'inline-grid' : 'grid';
}, function (props) {
  return buildPixelSize(props.styled.gap);
}, function (props) {
  return buildPixelSize(props.styled.pad);
}, function (props) {
  return props.styled.align || 'center';
}, function (props) {
  return props.styled.justifyContent;
}, function (props) {
  return props.styled.justifyItems;
}, function (props) {
  return props.styled.flow || 'row';
}, function (props) {
  return props.styled.columns;
}, function (props) {
  return props.styled.rows;
}, function (props) {
  return props.styled.templateColumns;
}, function (props) {
  return props.styled.height;
}, function (props) {
  return props.styled.maxWidth;
}, function (props) {
  if (props.styled.flow === 'row') {
    return {
      gridTemplateRows: props.styled.sizing
    };
  } else if (props.styled.flow === 'column') {
    return {
      gridTemplateColumns: props.styled.sizing
    };
  }

  return null;
});
var VStack = function VStack(props) {
  return createElement(Stack, Object.assign({}, props, {
    flow: "row"
  }));
};
var HStack = function HStack(props) {
  return createElement(Stack, Object.assign({}, props, {
    flow: "column"
  }));
};

var _templateObject$5;
var Center = function Center(_ref) {
  var children = _ref.children,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '100%' : _ref$height;
  return React__default.createElement(Centered, {
    height: height
  }, children);
};
var Centered = /*#__PURE__*/styled.div(_templateObject$5 || (_templateObject$5 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    display: grid;\n    height: ", ";\n    align-items: center;\n    justify-content: center;\n"])), function (props) {
  return props.height;
});

var GraphicWrapper = function GraphicWrapper(_ref) {
  var children = _ref.children,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? '96' : _ref$height,
      _ref$width = _ref.width,
      width = _ref$width === void 0 ? '96' : _ref$width,
      ariaLabel = _ref.ariaLabel,
      _ref$viewBox = _ref.viewBox,
      viewBox = _ref$viewBox === void 0 ? '0 0 96 96' : _ref$viewBox;
  return createElement("svg", {
    role: "img",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    height: height,
    width: width,
    "aria-label": ariaLabel,
    viewBox: viewBox
  }, children);
};

var DocumentGraphic = function DocumentGraphic(props) {
  return React__default.createElement(GraphicWrapper, Object.assign({}, props), React__default.createElement("circle", {
    cx: "48",
    cy: "48",
    r: "48",
    fill: color.Grey.G10
  }), React__default.createElement("g", {
    filter: "url(#documentGraphic_back)"
  }, React__default.createElement("rect", {
    x: "24.8906",
    y: "33",
    width: "35.7535",
    height: "46.0461",
    rx: "3",
    transform: "rotate(-7.92717 24.8906 33)",
    fill: color.Blue.B20
  }), React__default.createElement("rect", {
    x: "26.019",
    y: "33.8525",
    width: "33.7535",
    height: "44.0461",
    rx: "2",
    transform: "rotate(-7.92717 26.019 33.8525)",
    stroke: color.Blue.B60,
    strokeWidth: "2"
  })), React__default.createElement("g", {
    filter: "url(#documentGraphic_front)"
  }, React__default.createElement("path", {
    d: "M37.7558 65H67.8822C68.9929 65 69.8906 64.1558 69.8906 63.124V31.2313L59.8485 21.8512H37.7558C36.6451 21.8512 35.7473 22.6954 35.7473 23.7272V63.124C35.7473 64.1558 36.6451 65 37.7558 65Z",
    fill: "white",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M59.8906 22V29.2C59.8906 30.19 60.7846 31 61.8906 31H69.8906L59.8906 22Z",
    fill: "white",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), React__default.createElement("path", {
    d: "M41.8906 32H54.8919",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  }), React__default.createElement("path", {
    d: "M41.8906 60H54.8919",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  }), React__default.createElement("path", {
    d: "M41.8906 53H64.6428",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  }), React__default.createElement("path", {
    d: "M41.8906 46H64.6428",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  }), React__default.createElement("path", {
    d: "M41.8906 39H64.6428",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  }), React__default.createElement("defs", null, React__default.createElement("filter", {
    id: "documentGraphic_back",
    x: "20.2753",
    y: "25.4538",
    width: "50.9928",
    height: "59.7676",
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, React__default.createElement("feFlood", {
    floodOpacity: "0",
    result: "BackgroundImageFix"
  }), React__default.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    type: "matrix",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), React__default.createElement("feOffset", {
    dy: "2"
  }), React__default.createElement("feGaussianBlur", {
    stdDeviation: "2.5"
  }), React__default.createElement("feColorMatrix", {
    type: "matrix",
    values: "0 0 0 0 0.152941 0 0 0 0 0.156863 0 0 0 0 0.2 0 0 0 0.2 0"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  })), React__default.createElement("filter", {
    id: "documentGraphic_front",
    x: "29.7473",
    y: "17.8512",
    width: "46.1434",
    height: "55.1488",
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, React__default.createElement("feFlood", {
    floodOpacity: "0",
    result: "BackgroundImageFix"
  }), React__default.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    type: "matrix",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), React__default.createElement("feOffset", {
    dy: "2"
  }), React__default.createElement("feGaussianBlur", {
    stdDeviation: "2.5"
  }), React__default.createElement("feColorMatrix", {
    type: "matrix",
    values: "0 0 0 0 0.152941 0 0 0 0 0.156863 0 0 0 0 0.2 0 0 0 0.2 0"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  }))));
};

var CalendarGraphic = function CalendarGraphic(props) {
  return React__default.createElement(GraphicWrapper, Object.assign({}, props), React__default.createElement("circle", {
    cx: "48",
    cy: "48",
    r: "48",
    fill: color.Grey.G10
  }), React__default.createElement("g", {
    filter: "url(#calendarGraphic_back)"
  }, React__default.createElement("path", {
    d: "M55.1555 31.2858L60.6356 30.31C61.6403 30.1311 62.6087 30.8068 62.7876 31.8114L68.6423 64.6923C68.8211 65.697 68.1455 66.6653 67.1408 66.8442L28.7798 73.6747C27.7751 73.8536 26.8068 73.1779 26.6279 72.1733L20.7732 39.2924C20.5943 38.2877 21.2699 37.3194 22.2746 37.1405L27.7548 36.1647",
    fill: color.Blue.B20
  }), React__default.createElement("path", {
    d: "M55.1555 31.2858L60.6356 30.31C61.6403 30.1311 62.6087 30.8068 62.7876 31.8114L68.6423 64.6923C68.8211 65.697 68.1455 66.6654 67.1408 66.8442L28.7798 73.6747C27.7751 73.8536 26.8068 73.1779 26.6279 72.1733L20.7732 39.2924C20.5943 38.2877 21.2699 37.3194 22.2746 37.1405L27.7548 36.1647",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M30.652 31.6932L31.9531 39L30.652 31.6932Z",
    fill: color.Blue.B20
  }), React__default.createElement("path", {
    d: "M30.652 31.6932L31.9531 39",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), React__default.createElement("g", {
    filter: "url(#calendarGraphic_front)"
  }, React__default.createElement("path", {
    d: "M65.2066 26.8911H70.7729C71.7934 26.8911 72.6283 27.7261 72.6283 28.7466V62.1446C72.6283 63.1651 71.7934 64 70.7729 64H31.8086C30.7881 64 29.9531 63.1651 29.9531 62.1446V28.7466C29.9531 27.7261 30.7881 26.8911 31.8086 26.8911H37.3749",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M65.2066 26.8911H70.7729C71.7934 26.8911 72.6283 27.7261 72.6283 28.7466V62.1446C72.6283 63.1651 71.7934 64 70.7729 64H31.8086C30.7881 64 29.9531 63.1651 29.9531 62.1446V28.7466C29.9531 27.7261 30.7881 26.8911 31.8086 26.8911H37.3749",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M40.9531 22.5782V30V22.5782Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M40.9531 22.5782V30",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M61.9531 22.5782V30V22.5782Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M61.9531 22.5782V30",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M49.9531 38H51.8086H49.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M49.9531 38H51.8086",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M59.9531 38H61.8086H59.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M59.9531 38H61.8086",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M40.9531 47H42.8086H40.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M40.9531 47H42.8086",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M49.9531 47H51.8086H49.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M49.9531 47H51.8086",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M59.9531 47H61.8086H59.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M59.9531 47H61.8086",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M40.9531 56H42.8086H40.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M40.9531 56H42.8086",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M49.9531 56H51.8086H49.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M49.9531 56H51.8086",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M44.9531 27H57.9412H44.9531Z",
    fill: "white"
  }), React__default.createElement("path", {
    d: "M44.9531 27H57.9412",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), React__default.createElement("defs", null, React__default.createElement("filter", {
    id: "calendarGraphic_back",
    x: "14.7443",
    y: "26.2811",
    width: "59.9269",
    height: "55.4225",
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, React__default.createElement("feFlood", {
    floodOpacity: "0",
    result: "BackgroundImageFix"
  }), React__default.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    type: "matrix",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), React__default.createElement("feOffset", {
    dy: "2"
  }), React__default.createElement("feGaussianBlur", {
    stdDeviation: "2.5"
  }), React__default.createElement("feColorMatrix", {
    type: "matrix",
    values: "0 0 0 0 0.152941 0 0 0 0 0.156863 0 0 0 0 0.2 0 0 0 0.2 0"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  })), React__default.createElement("filter", {
    id: "calendarGraphic_front",
    x: "23.9531",
    y: "18.5782",
    width: "54.6752",
    height: "53.4218",
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, React__default.createElement("feFlood", {
    floodOpacity: "0",
    result: "BackgroundImageFix"
  }), React__default.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    type: "matrix",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), React__default.createElement("feOffset", {
    dy: "2"
  }), React__default.createElement("feGaussianBlur", {
    stdDeviation: "2.5"
  }), React__default.createElement("feColorMatrix", {
    type: "matrix",
    values: "0 0 0 0 0.152941 0 0 0 0 0.156863 0 0 0 0 0.2 0 0 0 0.2 0"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  }))));
};

var SearchGraphic = function SearchGraphic(props) {
  return React__default.createElement(GraphicWrapper, Object.assign({}, props), React__default.createElement("circle", {
    cx: "48",
    cy: "48",
    r: "48",
    fill: color.Grey.G10
  }), React__default.createElement("g", {
    filter: "url(#searchGraphic_document)"
  }, React__default.createElement("path", {
    d: "M28.7937 69H55.6999C56.6918 69 57.4936 68.246 57.4936 67.3245V38.841L48.5249 30.4635H28.7937C27.8018 30.4635 27 31.2175 27 32.139V67.3245C27 68.246 27.8018 69 28.7937 69Z",
    fill: color.Blue.B20,
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M32.4863 64.5345H44.0978",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  }), React__default.createElement("path", {
    d: "M32.4863 58.2828H52.8064",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  }), React__default.createElement("path", {
    d: "M32.4863 52.031H52.8064",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeLinecap: "round"
  })), React__default.createElement("g", {
    filter: "url(#sesearchGraphic_magnifier)"
  }, React__default.createElement("path", {
    d: "M66.8878 38.9439C66.8878 47.7485 59.7485 54.8879 50.9439 54.8879C42.1393 54.8879 34.9999 47.7485 34.9999 38.9439C34.9999 30.1393 42.1393 23 50.9439 23C59.7485 23 66.8878 30.1393 66.8878 38.9439Z",
    fill: "white",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M73.572 65.3277L66.0607 57.8165C65.0155 56.7712 65.0155 55.0882 66.0607 54.0607C67.0882 53.0155 68.7712 53.0155 69.8164 54.0607L77.3277 61.572C78.3729 62.6172 78.3729 64.3002 77.3277 65.3277C76.3002 66.3729 74.6172 66.3729 73.572 65.3277Z",
    fill: "white",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }), React__default.createElement("path", {
    d: "M66.1671 54.1674L62.6052 50.6054",
    stroke: color.Blue.B60,
    strokeWidth: "2",
    strokeMiterlimit: "10",
    strokeLinecap: "round",
    strokeLinejoin: "round"
  })), React__default.createElement("defs", null, React__default.createElement("filter", {
    id: "searchGraphic_document",
    x: "21",
    y: "26.4635",
    width: "42.4936",
    height: "50.5365",
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, React__default.createElement("feFlood", {
    floodOpacity: "0",
    result: "BackgroundImageFix"
  }), React__default.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    type: "matrix",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), React__default.createElement("feOffset", {
    dy: "2"
  }), React__default.createElement("feGaussianBlur", {
    stdDeviation: "2.5"
  }), React__default.createElement("feColorMatrix", {
    type: "matrix",
    values: "0 0 0 0 0.152941 0 0 0 0 0.156863 0 0 0 0 0.2 0 0 0 0.2 0"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  })), React__default.createElement("filter", {
    id: "sesearchGraphic_magnifier",
    x: "28.9999",
    y: "19",
    width: "55.1117",
    height: "55.1116",
    filterUnits: "userSpaceOnUse",
    colorInterpolationFilters: "sRGB"
  }, React__default.createElement("feFlood", {
    floodOpacity: "0",
    result: "BackgroundImageFix"
  }), React__default.createElement("feColorMatrix", {
    "in": "SourceAlpha",
    type: "matrix",
    values: "0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
  }), React__default.createElement("feOffset", {
    dy: "2"
  }), React__default.createElement("feGaussianBlur", {
    stdDeviation: "2.5"
  }), React__default.createElement("feColorMatrix", {
    type: "matrix",
    values: "0 0 0 0 0.152941 0 0 0 0 0.156863 0 0 0 0 0.2 0 0 0 0.2 0"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    in2: "BackgroundImageFix",
    result: "effect1_dropShadow"
  }), React__default.createElement("feBlend", {
    mode: "normal",
    "in": "SourceGraphic",
    in2: "effect1_dropShadow",
    result: "shape"
  }))));
};

var Graphic = function Graphic(_ref) {
  var type = _ref.type,
      props = _objectWithoutPropertiesLoose(_ref, ["type"]);

  return React__default.createElement(React__default.Fragment, null, type === 'Calendar' && React__default.createElement(CalendarGraphic, Object.assign({}, props)), type === 'Document' && React__default.createElement(DocumentGraphic, Object.assign({}, props)), type === 'Search' && React__default.createElement(SearchGraphic, Object.assign({}, props)));
};

var EmptyState = function EmptyState(_ref) {
  var buttonCaption = _ref.buttonCaption,
      description = _ref.description,
      graphic = _ref.graphic,
      onClick = _ref.onClick,
      title = _ref.title,
      variant = _ref.variant,
      showButton = _ref.showButton,
      addHash = _ref.addHash;

  if (variant === 'Large') {
    return React__default.createElement(VStack, {
      gap: "small",
      align: "center",
      justifyContent: "center"
    }, React__default.createElement(Center, null, React__default.createElement(Graphic, {
      type: graphic,
      ariaLabel: title
    })), React__default.createElement(Center, null, React__default.createElement(Text, {
      addHash: addHash,
      renderAs: "h4",
      variant: "large.bold"
    }, title)), React__default.createElement("div", {
      style: {
        maxWidth: '35ch',
        textAlign: 'center',
        margin: '0 auto'
      }
    }, React__default.createElement(Text, {
      renderAs: "p",
      variant: "normal.regular"
    }, description)), showButton && React__default.createElement(Center, null, React__default.createElement(Button, {
      compact: true,
      onClick: onClick,
      variant: "Tertiary"
    }, buttonCaption)));
  } else {
    return React__default.createElement(HStack, {
      gap: "medium",
      sizing: "max-content"
    }, React__default.createElement(Center, null, React__default.createElement(Graphic, {
      type: graphic,
      ariaLabel: title
    })), React__default.createElement(VStack, {
      gap: "xSmall"
    }, React__default.createElement("div", null, React__default.createElement(Text, {
      addHash: addHash,
      renderAs: "h4",
      variant: "large.bold"
    }, title)), React__default.createElement("div", {
      style: {
        maxWidth: '35ch'
      }
    }, React__default.createElement(Text, {
      renderAs: "p",
      variant: "normal.regular"
    }, description)), showButton && React__default.createElement("div", null, React__default.createElement(Button, {
      compact: true,
      onClick: onClick,
      variant: "Tertiary"
    }, buttonCaption))));
  }
};

var useClickOutside = function useClickOutside(ref, callback) {
  var handleClick = function handleClick(e) {
    if (ref.current && !ref.current.contains(e.target)) {
      callback();
    }
  };

  React__default.useEffect(function () {
    document.addEventListener('click', handleClick);
    return function () {
      document.removeEventListener('click', handleClick);
    };
  });
};

var useSearch = function useSearch(_ref) {
  var onCancel = _ref.onCancel,
      onSearch = _ref.onSearch,
      _ref$debounce = _ref.debounce,
      debounce = _ref$debounce === void 0 ? 500 : _ref$debounce;
  var inputRef = createRef();
  var cancelButtonRef = createRef();
  var searchTimeout = React__default.useRef(0);
  var searching = useRef(false);
  var hasSearched = useRef(false);
  React__default.useEffect(function () {
    return function () {
      return window.clearTimeout(searchTimeout.current);
    };
  }, []);

  var handleSearch = function handleSearch(search) {
    if (!searching.current) {
      searching.current = true;

      if ((search == null ? void 0 : search.length) > 0) {
        hasSearched.current = true;
        onSearch(search)["finally"](function () {
          searching.current = false;
        });
      } else {
        if (hasSearched.current === true) {
          hasSearched.current = false;
          onCancel()["finally"](function () {
            searching.current = false;
          });
        }
      }
    }
  };

  var handleInputOnChange = function handleInputOnChange(e) {
    window.clearTimeout(searchTimeout.current);

    if (e.target.value.length > 0) {
      cancelButtonRef.current.style.visibility = 'visible';
    } else {
      cancelButtonRef.current.style.visibility = 'hidden';
    }
  };

  var onKeyUp = function onKeyUp(e) {
    window.clearTimeout(searchTimeout.current);
    var value = e.target.value;

    if (isEscape(e)) {
      handleSearch('');
    } else if (isEnter(e)) {
      handleSearch(value);
    } else {
      if (value.length >= 3) {
        searchTimeout.current = window.setTimeout(function () {
          window.clearTimeout(searchTimeout.current);
          handleSearch(value);
        }, debounce);
      }

      if (value.length === 0) {
        searchTimeout.current = window.setTimeout(function () {
          window.clearTimeout(searchTimeout.current);
          handleSearch('');
        }, debounce);
      }
    }
  };

  return {
    input: {
      onKeyUp: onKeyUp,
      onChange: handleInputOnChange,
      onClick: function onClick(e) {
        e.target.select();
      },
      type: 'search',
      ariaLabel: 'Search the grid',
      placeholder: 'Search',
      ref: inputRef
    },
    searchButton: {
      onClick: function onClick() {
        return handleSearch(inputRef.current.value);
      },
      title: 'Search the grid',
      ariaLabel: 'Search the grid',
      type: 'button'
    },
    cancelButton: {
      onClick: function onClick() {
        inputRef.current.value = '';
        handleSearch();
        inputRef.current.focus();
      },
      title: 'Clear Search',
      ariaLabel: 'Clear Search',
      type: 'button',
      style: {
        visibility: 'hidden'
      },
      ref: cancelButtonRef
    }
  };
};

var useWindowSize = function useWindowSize() {
  var _useState = useState({
    width: undefined,
    height: undefined
  }),
      windowSize = _useState[0],
      setWindowSize = _useState[1];

  useEffect(function () {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();
    return function () {
      return window.removeEventListener('resize', handleResize);
    };
  }, []);
  return windowSize;
};

function useSRW() {
  return {
    popupVisibility: function popupVisibility(visibility) {
      if (visibility === 'Show') {
        document.body.classList.add('popup-shown');
      } else if (visibility = 'Hide') {
        document.body.classList.remove('popup-shown');
      }
    },
    globalLoading: function globalLoading(loading) {
      var elements = document.getElementsByClassName('ui-global');

      if (elements && elements.length > 0) {
        if (loading) {
          elements[0].classList.add('loading');
        } else {
          elements[0].classList.remove('loading');
        }
      }
    }
  };
}

var HashContent = function HashContent(_ref) {
  var hash = _ref.hash,
      children = _ref.children;

  var _useHash = useHash(true),
      setHashAtt = _useHash.setHashAtt;

  return React__default.createElement("div", Object.assign({}, setHashAtt(hash)), children);
};

var Sticky = function Sticky(_ref) {
  var children = _ref.children,
      _ref$zIndex = _ref.zIndex,
      zIndex = _ref$zIndex === void 0 ? 1 : _ref$zIndex,
      props = _objectWithoutPropertiesLoose(_ref, ["children", "zIndex"]);

  return React__default.createElement("div", {
    style: _extends({
      zIndex: zIndex,
      position: 'sticky'
    }, props)
  }, children);
};

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var runtime_1 = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var runtime = (function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }
  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function(obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  exports.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  IteratorPrototype[iteratorSymbol] = function () {
    return this;
  };

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = Gp.constructor = GeneratorFunctionPrototype;
  GeneratorFunctionPrototype.constructor = GeneratorFunction;
  GeneratorFunction.displayName = define(
    GeneratorFunctionPrototype,
    toStringTagSymbol,
    "GeneratorFunction"
  );

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      define(prototype, method, function(arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  exports.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  exports.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function(error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  AsyncIterator.prototype[asyncIteratorSymbol] = function () {
    return this;
  };
  exports.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  exports.async = function(innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;

    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList),
      PromiseImpl
    );

    return exports.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  define(Gp, toStringTagSymbol, "Generator");

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  Gp[iteratorSymbol] = function() {
    return this;
  };

  Gp.toString = function() {
    return "[object Generator]";
  };

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  exports.values = values;

  function doneResult() {
    return { value: undefined$1, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined$1;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  };

  // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.
  return exports;

}(
  // If this script is executing as a CommonJS module, use module.exports
  // as the regeneratorRuntime namespace. Otherwise create a new empty
  // object. Either way, the resulting object will be used to initialize
  // the regeneratorRuntime variable at the top of this file.
   module.exports 
));

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  Function("r", "regeneratorRuntime = r")(runtime);
}
});

var _templateObject$6, _templateObject2$2;
var ExpansionPanel = function ExpansionPanel(_ref) {
  var buttonAriaLabel = _ref.buttonAriaLabel,
      buttonTitle = _ref.buttonTitle,
      caption = _ref.caption,
      children = _ref.children,
      expanded = _ref.expanded,
      headerContent = _ref.headerContent,
      _ref$height = _ref.height,
      height = _ref$height === void 0 ? 'auto' : _ref$height,
      onChange = _ref.onChange,
      addHash = _ref.addHash;

  var _React$useState = useState(expanded),
      isExpanded = _React$useState[0],
      setIsExpanded = _React$useState[1];

  var controls = useAnimation();
  useEffect(function () {
    if (expanded !== undefined) {
      setIsExpanded(Boolean(expanded));
    }
  }, [expanded]);
  var headerHeight = 55;
  var gap = 16;
  var panelHeight = height !== 'auto' ? height - (headerHeight + gap) : 'auto';

  var setControlCollapsed = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(value) {
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              onChange && onChange(value);
              setIsExpanded(value);

              if (!value) {
                _context.next = 10;
                break;
              }

              _context.next = 5;
              return controls.start({
                height: panelHeight
              });

            case 5:
              _context.next = 7;
              return controls.start({
                opacity: 1
              });

            case 7:
              return _context.abrupt("return", _context.sent);

            case 10:
              _context.next = 12;
              return controls.start({
                opacity: 0,
                height: 0
              });

            case 12:
              return _context.abrupt("return", _context.sent);

            case 13:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function setControlCollapsed(_x) {
      return _ref2.apply(this, arguments);
    };
  }();

  var handleOnCollapsedClick = function handleOnCollapsedClick() {
    setControlCollapsed(!isExpanded);
  };

  return createElement(Wrapper, null, createElement("div", {
    style: {
      minHeight: isExpanded ? height : headerHeight,
      display: 'grid',
      gridGap: isExpanded ? gap : 0,
      gridTemplateRows: headerHeight + "px auto"
    }
  }, createElement(HStack, {
    columns: "auto 1fr",
    gap: "xSmall"
  }, createElement("div", null, createElement(motion.div, {
    initial: {
      rotate: isExpanded ? 0 : -90
    },
    animate: {
      rotate: isExpanded ? 0 : -90
    }
  }, createElement("div", null, createElement(Button$1, {
    title: buttonTitle,
    onClick: handleOnCollapsedClick,
    "aria-label": buttonAriaLabel
  }, createElement(Icon, {
    ariaLabel: buttonAriaLabel,
    icon: "ChevronDown"
  }))))), createElement(HStack, {
    columns: "1fr auto"
  }, createElement(Text, {
    addHash: addHash,
    renderAs: "p",
    variant: "large.bold"
  }, caption), createElement("div", null, headerContent))), createElement(motion.div, {
    animate: controls
  }, isExpanded && createElement("div", {
    style: {
      overflow: 'hidden',
      height: panelHeight
    }
  }, children))));
};
var Wrapper = /*#__PURE__*/styled.div(_templateObject$6 || (_templateObject$6 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    border: 1px solid ", ";\n    padding: 0 16px;\n"])), color.Grey.G30);
var Button$1 = /*#__PURE__*/styled.button(_templateObject2$2 || (_templateObject2$2 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    height: 30px;\n    width: 30px;\n    padding: 0;\n    margin: 0;\n    display: flex;\n    background-color: transparent;\n    cursor: pointer;\n    border: 0;\n    color: ", ";\n    border-radius: 3px;\n    align-items: center;\n    justify-content: center;\n\n    * {\n        cursor: pointer;\n    }\n\n    &:focus,\n    &:hover {\n        background-color: ", ";\n        color: ", ";\n    }\n\n    &:focus {\n        outline: ", " solid 2px;\n        outline-offset: -2px;\n    }\n"])), color.Grey.G70, color.Blue.B20, color.Blue.B80, color.Blue.B80);

var _templateObject$7;
var Panel = function Panel(_ref) {
  var children = _ref.children,
      _ref$pad = _ref.pad,
      pad = _ref$pad === void 0 ? 'small' : _ref$pad;
  return React__default.createElement(Wrapper$1, {
    pad: pad
  }, children);
};
var Wrapper$1 = /*#__PURE__*/styled.div(_templateObject$7 || (_templateObject$7 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    border: 1px solid ", ";\n    padding: ", ";\n"])), color.Grey.G30, function (props) {
  return buildPixelSize(props.pad);
});

var _templateObject$8;
var ContentTabButton = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var tabPanelId = _ref.tabPanelId,
      caption = _ref.caption,
      isActive = _ref.isActive,
      onClick = _ref.onClick,
      onKeyDown = _ref.onKeyDown,
      addHash = _ref.addHash;
  return createElement("li", {
    role: "presentation"
  }, createElement(Button$2, {
    ref: ref,
    role: "tab",
    type: "button",
    "aria-selected": isActive,
    "aria-controls": tabPanelId,
    tabIndex: isActive ? 0 : -1,
    onClick: onClick,
    onKeyDown: onKeyDown
  }, createElement(Text, {
    addHash: addHash,
    variant: "normal.medium"
  }, caption)));
});
var Button$2 = /*#__PURE__*/styled.button(_templateObject$8 || (_templateObject$8 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    padding: calc(8px + 2px) 16px 8px;\n    font-weight: 500;\n    background-color: transparent;\n    border: 0;\n    outline: 0;\n    user-select: none;\n    transition: background-color ease-in-out 0.15s, color ease-in-out 0.15s;\n    cursor: pointer;\n\n    > * {\n        cursor: pointer;\n    }\n\n    &[aria-selected='true'] {\n        border-bottom: 2px solid ", ";\n    }\n\n    &[aria-selected='false'] {\n        padding-bottom: calc(8px + 2px);\n    }\n\n    &:focus {\n        color: ", ";\n        outline: 2px solid ", ";\n        outline-offset: -2px;\n    }\n\n    &[aria-selected='false']:hover {\n        color: ", ";\n        background-color: ", ";\n\n        &:focus {\n            outline: 2px solid ", ";\n            outline-offset: -2px;\n        }\n    }\n"])), color.Blue.B60, color.Blue.B60, color.Blue.B60, color.Blue.B60, color.Blue.B20, color.Blue.B60);

var _templateObject$9, _templateObject2$3;

function guardTabIndex(length, i) {
  if (i && i > 0 && i < length) {
    return i;
  }

  return 0;
}

var ContentTabs = function ContentTabs(_ref) {
  var ariaLabel = _ref.ariaLabel,
      children = _ref.children,
      onChange = _ref.onChange,
      tabIndex = _ref.tabIndex,
      autoFocus = _ref.autoFocus,
      addHash = _ref.addHash,
      stickyTabs = _ref.stickyTabs;
  var pages = Children.toArray(children).map(function (child, index) {
    return _extends({}, child.props, {
      index: index,
      key: v1(),
      ref: createRef()
    });
  }).filter(function (e) {
    return e;
  });

  var _React$useState = useState(guardTabIndex(pages.length, tabIndex)),
      activeTabIndex = _React$useState[0],
      setActiveTabIndex = _React$useState[1];

  var focus = useRef(guardTabIndex(pages.length, tabIndex));
  useEffect(function () {
    if (autoFocus) {
      var _pages$focus$current, _pages$focus$current$;

      (_pages$focus$current = pages[focus.current]) == null ? void 0 : (_pages$focus$current$ = _pages$focus$current.ref.current) == null ? void 0 : _pages$focus$current$.focus();
    }
  }, [autoFocus, pages]);
  useEffect(function () {
    var _pages$focus$current2, _pages$focus$current3;

    (_pages$focus$current2 = pages[focus.current]) == null ? void 0 : (_pages$focus$current3 = _pages$focus$current2.ref.current) == null ? void 0 : _pages$focus$current3.focus();
  }, [pages]);

  if (pages.length === 0) {
    return createElement("div", {
      style: _extends({}, scheme.Red.Lighter, {
        padding: '8px'
      })
    }, createElement(Text, null, "This component requires a child element of ContentTabPage."));
  }

  var handleKeyDown = function handleKeyDown(e) {
    var setFocus = false;
    var focusIndex = focus.current;

    if (isDownArrow(e) || isUpArrow(e)) {
      e.preventDefault();
    } else if (isHome(e)) {
      setFocus = true;
      focusIndex = 0;
    } else if (isEnd(e)) {
      setFocus = true;
      focusIndex = pages.length - 1;
    } else if (isLeftArrow(e)) {
      if (focusIndex > 0 && focusIndex < pages.length) {
        --focusIndex;
        setFocus = true;
      }
    } else if (isRightArrow(e)) {
      if (focusIndex >= 0 && focusIndex < pages.length - 1) {
        ++focusIndex;
        setFocus = true;
      }
    }

    if (setFocus) {
      var _pages$focusIndex, _pages$focusIndex$ref;

      (_pages$focusIndex = pages[focusIndex]) == null ? void 0 : (_pages$focusIndex$ref = _pages$focusIndex.ref.current) == null ? void 0 : _pages$focusIndex$ref.focus();
      focus.current = focusIndex;
    }
  };

  var TabBarButtons = function TabBarButtons() {
    return createElement(UL, {
      role: "tablist",
      "aria-label": ariaLabel
    }, pages.map(function (child) {
      var index = child.index,
          key = child.key,
          caption = child.caption,
          id = child.id,
          ref = child.ref;

      var handleClick = function handleClick() {
        focus.current = index;
        onChange && onChange({
          caption: caption,
          index: index,
          id: id
        });
        setActiveTabIndex(index);
      };

      return createElement(ContentTabButton, {
        key: key,
        ref: ref,
        tabPanelId: key,
        caption: caption,
        isActive: index === activeTabIndex,
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        addHash: addHash
      });
    }));
  };

  return createElement(Fragment, null, stickyTabs ? createElement(Sticky, Object.assign({}, stickyTabs), createElement(TabBarButtons, null)) : createElement(TabBarButtons, null), createElement(TabPanelDiv, {
    key: "tab-panel-" + pages[activeTabIndex].key,
    id: pages[activeTabIndex].key,
    role: "tabpanel",
    "aria-labelledby": pages[activeTabIndex].key,
    tabIndex: 0
  }, createElement(Fragment, null, pages[activeTabIndex].children)));
};
var UL = /*#__PURE__*/styled.ul(_templateObject$9 || (_templateObject$9 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    padding: 0;\n    margin: 0;\n    display: flex;\n    list-style: none;\n    border-bottom: 1px solid ", ";\n"])), color.Grey.G20);
var TabPanelDiv = /*#__PURE__*/styled.div(_templateObject2$3 || (_templateObject2$3 = /*#__PURE__*/_taggedTemplateLiteralLoose(["\n    &:focus,\n    &:active {\n        border: none;\n        outline: none;\n    }\n"])));

var ContentTabPage = function ContentTabPage() {
  return createElement(Fragment, null);
};

export { AnalyticsProvider, Button, Center, ContentSwitcher, ContentTabPage, ContentTabs, EmptyState, ExpansionPanel, FontTypes, Graphic, HStack, HashContent, Icon, Panel, Stack, Sticky, SwitcherButton, Text, VStack, color, font, fontTypeToObject, isDownArrow, isEnd, isEnter, isEscape, isHome, isKey, isLeftArrow, isMinus, isPageDown, isPageUp, isPeriod, isRightArrow, isSpace, isUpArrow, scheme, size, styles, useAnalytics, useClickOutside, useHash, usePerformance, useSRW, useSearch, useSentry, useWindowSize };
//# sourceMappingURL=starryui9.esm.js.map
